<template>
  <div class="encounter-notes-center bg-[#f9fbfd] min-h-full">
    <!-- Page Title -->
    <h1
      class="page-title text-[22px] text-[#333] leading-6 font-medium xl:pl-[76px] pl-[60px] pt-5 pb-4"
      style="font-family: 'Avenir', sans-serif"
    >
      Encounter Notes Center
    </h1>

    <!-- Main Content Card -->
    <div class="mx-6 bg-white rounded xl:p-6 p-4 flex-1 overflow-x-auto">
      <div class="flex flex-col gap-5">
        <!-- Header: Search + Filters + Create Button -->
        <div
          class="flex items-center justify-between pb-4 border-b border-[#e0e0e0] flex-wrap gap-x-6 gap-y-4"
        >
          <!-- Search and Filters -->
          <div class="flex items-end gap-6 flex-wrap flex-1 min-w-0">
            <!-- Search Field -->
            <div class="flex items-center gap-2 min-w-[280px] max-w-[515px] flex-1">
              <div class="flex items-center gap-1">
                <img
                  src="@/assets/encounterNotes/search.svg"
                  alt="Search"
                  class="xl:w-6 xl:h-6 w-6 h-6"
                />
                <span
                  class="text-sm text-[#4a4a4a] font-medium tracking-[0.1px] leading-5"
                  style="font-family: 'Inter', sans-serif"
                >
                  Search
                </span>
              </div>
              <div class="flex-1">
                <InputQuestion
                  v-model="searchQuery"
                  placeholder="Search Patient Name, Notes..."
                  class="search-input"
                />
              </div>
            </div>

            <!-- Filter Dropdowns -->
            <div class="flex items-center gap-3">
              <!-- Appointment Type Filter -->
              <DropdownQuestion
                v-model="selectedAppointmentType"
                :options="appointmentTypeOptions"
                placeholder="Appointment Type"
                class="min-w-[180px]"
                select-width="100%"
              />

              <!-- Visit Type Filter -->
              <!-- <SingleSelector
                v-model="selectedVisitType"
                :options="visitTypeOptions"
                placeholder="Visit Type"
                class="min-w-[150px]"
              /> -->
            </div>
          </div>

          <!-- Create Button -->

          <ResponsiveButton
            class="min-w-[194px]"
            @click-button="handleCreateNote"
          >
            <template #prefix>
              <img
                src="@/assets/encounterNotes/add.svg"
                alt="Add"
                class="w-6 h-6"
              />
            </template>
            Create Encounter Notes
          </ResponsiveButton>
        </div>

        <!-- Loading State -->
        <div
          v-if="loading"
          class="flex items-center justify-center min-h-[calc(100vh-300px)]"
        >
          <div class="flex flex-col items-center gap-4">
            <div class="loading-spinner"></div>
            <p
              class="text-base text-[#979797] font-medium"
              style="font-family: 'Inter', sans-serif"
            >
              Loading notes...
            </p>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="notes.length === 0"
          class="empty-state flex flex-col items-center justify-center min-h-[calc(100vh-300px)]"
        >
          <img
            src="@/assets/encounterNotes/empty-notes.svg"
            alt="No notes"
            class="w-[203px] h-[136px] mb-6"
          />
          <p
            class="text-lg text-black font-medium mb-2"
            style="font-family: 'Inter', sans-serif"
          >
            No encounter notes created / found
          </p>
          <p
            class="text-base text-[#979797] font-medium"
            style="font-family: 'Inter', sans-serif"
          >
            Create one to get started.
          </p>
        </div>

        <!-- Notes Table -->
        <template v-else>
          <EncounterNotesTable
            :notes="notes"
            :inline-text-map="inlineTextMap"
            @edit="handleEdit"
            @deleted="handleDeleted"
            @download="handleDownload"
          />

          <!-- Pagination -->
          <NotePagination
            v-model:currentPage="currentPage"
            v-model:perPage="perPage"
            :totalItems="totalItems"
          />
        </template>
      </div>
    </div>

    <!-- Create Note Modal -->
    <SwitchPatientModal
      v-model="showCreateModal"
      :default-patient-id="patientIdFromQuery"
      :default-patient-name="patientNameFromQuery"
      title="Create a new note"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import EncounterNotesTable from "./components/EncounterNotesTable.vue";
import NotePagination from "./components/NotePagination.vue";
import DropdownQuestion from "@/views/setting/my-practice/components/DropdownQuestion.vue";
import InputQuestion from "@/views/setting/my-practice/components/InputQuestion.vue";
import SwitchPatientModal from "./components/SwitchPatientModal.vue";
import { getEncounterNotesListApi } from "@/api/encounterNotes.js";
import {
  normalizeNotesListResponse,
  getPatientById,
  getPatientFullName,
} from "./utils/encounterNotes";
import { generateNotePdf } from "./utils/generateNotePdf";
import { useActionCardsInlineText } from "./composables/useActionCardsInlineText";
import { debounce } from "lodash";

const { inlineTextMap, fetchAllInlineText } = useActionCardsInlineText();

const router = useRouter();
const route = useRoute();
const store = useStore();

// Patient info from URL query
const patientIdFromQuery = ref(route.query.patientId || "");
const patientNameFromQuery = ref("");

// Remove query params from URL after saving them
if (route.query.patientId) {
  router.replace({ path: route.path, query: {} });
}

// Search and filters
const searchQuery = ref("");
const selectedAppointmentType = ref("");

// Fetch patient name by ID
const fetchPatientName = async () => {
  if (!patientIdFromQuery.value) return;

  const patient = await getPatientById(patientIdFromQuery.value, store);
  if (patient) {
    patientNameFromQuery.value = getPatientFullName(patient);
    searchQuery.value = patientNameFromQuery.value;
  }
};

// Pagination
const currentPage = ref(1);
const perPage = ref(10);
const totalItems = ref(0);

// Notes data
const notes = ref([]);
const loading = ref(true);

// Filter options (format: { label, value })
const appointmentTypeOptions = computed(() => [
  { label: "General", value: "General" },
  { label: "Checkup", value: "Checkup" },
  { label: "Followup", value: "Followup" },
  { label: "Initial Consultation", value: "Initial Consultation" },
]);

// Fetch notes from API
const fetchNotes = async () => {
  loading.value = true;
  try {
    const params = {
      limit: perPage.value,
      offset: (currentPage.value - 1) * perPage.value,
      sort: "created_at",
      order: "desc",
    };

    // Use patientId from query params if available
    if (patientIdFromQuery.value) {
      params.patient_id = patientIdFromQuery.value;
    }

    // Add search query to API params
    // If filtering by patientId, remove patient name from search to avoid redundant filtering
    if (searchQuery.value) {
      let searchInput = searchQuery.value;
      if (patientIdFromQuery.value && patientNameFromQuery.value) {
        searchInput = searchInput
          .replace(patientNameFromQuery.value, "")
          .trim();
      }
      if (searchInput) {
        params.search_input = searchInput;
      }
    }

    // Add appointment type filter to API params
    if (selectedAppointmentType.value) {
      params.appointment_type = selectedAppointmentType.value;
    }

    const response = await getEncounterNotesListApi(params);
    const normalized = normalizeNotesListResponse(response);
    notes.value = normalized.notes;
    totalItems.value = normalized.total;
  } catch (error) {
    console.error("Failed to fetch encounter notes:", error);
    notes.value = [];
    totalItems.value = 0;
  } finally {
    loading.value = false;
  }
};

// Debounced fetch for search input
const debouncedFetchNotes = debounce(() => {
  currentPage.value = 1;
  fetchNotes();
}, 500);

onMounted(async () => {
  // Fetch inline text for action cards (shared across components)
  fetchAllInlineText();
  // Fetch patient name first if patientId is provided
  if (patientIdFromQuery.value) {
    await fetchPatientName();
  }
  await fetchNotes();
  // Clear initial load flag after first fetch to enable watcher
  isInitialLoad.value = false;
});

// Flag to prevent double fetch when perPage changes
const isPerPageChanging = ref(false);

// Flag to prevent double fetch during initial load
const isInitialLoad = ref(true);

// Re-fetch notes when pagination changes
watch(currentPage, () => {
  // Skip if this change was triggered by perPage update
  if (isPerPageChanging.value) return;
  fetchNotes();
});

watch(perPage, async () => {
  // Reset to first page when changing items per page
  isPerPageChanging.value = true;
  currentPage.value = 1;
  await fetchNotes();
  isPerPageChanging.value = false;
});

// Watch search query and filters - fetch from API when they change
watch(searchQuery, (newValue) => {
  // Skip during initial load to prevent double fetch
  if (isInitialLoad.value) return;

  // If search query no longer includes the patient name, clear patient filter
  if (
    patientNameFromQuery.value &&
    !newValue.includes(patientNameFromQuery.value)
  ) {
    patientIdFromQuery.value = "";
    patientNameFromQuery.value = "";
  }
  debouncedFetchNotes();
});

watch(selectedAppointmentType, () => {
  currentPage.value = 1;
  fetchNotes();
});

// Modal state
const showCreateModal = ref(false);

// Action handlers
const handleCreateNote = () => {
  showCreateModal.value = true;
};

const handleEdit = (note) => {
  // Edit action handled by EncounterNotesTable row click
};

const handleDeleted = () => {
  // Refresh the notes list after successful deletion
  fetchNotes();
};

const handleDownload = async (note) => {
  const patientId = note.patient_id;
  try {
    const noteData = {
      content: note.content,
      title: note.title,
      service_date: note.service_date,
      signed_at: note.signed_at,
    };
    await generateNotePdf(patientId, store, noteData);
  } catch (error) {
    console.error('Failed to generate PDF:', error);
    const { notify } = await import('@/utils/notify');
    notify({
      type: 'error',
      title: 'Download Failed',
      message: error.message || 'Failed to generate PDF.',
      duration: 5000,
    });
  }
};

// Clear patient filter when leaving page
onBeforeUnmount(() => {
  patientIdFromQuery.value = "";
  patientNameFromQuery.value = "";
  searchQuery.value = "";
});
</script>

<style scoped>
.encounter-notes-center {
  min-height: calc(100vh - var(--nav-height));
}

.page-title {
  letter-spacing: 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #004879;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
